import { Box, Grid, Stack, Typography } from "@mui/material";
import SaveEditButtons from "components/buttons/SaveEditButtons";
import { useForm } from "react-hook-form";
import Form from "components/form/Form";
import TextFieldControl from "components/form/TextFieldControl";
import { useState } from "react";
import useIsCreate from "hooks/useIsCreate";
import VALIDATION from "helpers/validation";
import { useCandidateInfoQuery } from "@generated/graphql";
import { useNavigate, useParams } from "react-router-dom";
import AutocompleteControl from "components/form/AutocompleteControl";
import { CANDIDATE_TYPE_OPTIONS, CandidateType, GRADE_OPTIONS } from "constants/global";
import SelectCandidateType from "components/SelectCandidateType";
import FileUploadButton from "components/buttons/FileUploadButton";
import ROUTES from "constants/routes";
import { CandidateInputs } from "../types";
import useUpdateCandidate from "../hooks/useUpdateCandidate";
import VacanciesList from "./VacanciesList";

const Content = () => {
  const { candidateId } = useParams();
  const isCreate = useIsCreate();
  const [isEditing, setIsEditing] = useState<boolean>(isCreate);
  const navigate = useNavigate();

  const { data, refetch } = useCandidateInfoQuery({ variables: { candidateId: candidateId! } });
  const companyCandidate = data?.companyCandidate;

  const onUpdateCandidateSuccess = (id: string) => {
    if (isCreate) {
      navigate(`/${ROUTES.CANDIDATE}/view/${id}`, { replace: true });
    } else {
      refetch();
    }
  };

  const { onUpdateCompanyCandidate, updateCompanyCandidateLoading } = useUpdateCandidate({
    setIsEditing,
    onSuccess: onUpdateCandidateSuccess,
  });

  const form = useForm<CandidateInputs>({
    values: {
      id: candidateId!,
      telegramUsername: companyCandidate?.candidateUsername?.telegramUsername || "",
      fullName: companyCandidate?.fullName || "",
      email: companyCandidate?.email || "",
      phoneNumber: companyCandidate?.candidateUsername?.candidate?.phoneNumber?.replace("+", ""),
      grade: GRADE_OPTIONS.find((grade) => grade.id === companyCandidate?.grade) || null,
      organization: companyCandidate?.organization || "",
      position: companyCandidate?.position || "",
      type: CANDIDATE_TYPE_OPTIONS.find((type) => type.id === companyCandidate?.type) || null,
      candidateType: companyCandidate?.candidateUsername?.login ? CandidateType.Login : CandidateType.Telegram,
      login: companyCandidate?.candidateUsername?.login || "",
      link: companyCandidate?.link || "",
      password: "",
      cvFile: companyCandidate?.cvFile,
    },
  });
  const { handleSubmit, watch } = form;
  const candidateType = watch("candidateType");
  const isTelegramUser = candidateType === CandidateType.Telegram;

  return (
    <Box
      sx={{
        backgroundColor: "white",
        borderRadius: 2,
        p: 2.5,
      }}>
      <Form form={form}>
        <Grid
          container
          spacing={2.5}>
          <Grid
            item
            xs={12}
            md={6}>
            <Stack spacing={2}>
              <SelectCandidateType isEditing={isEditing} />
              {isTelegramUser ? (
                <TextFieldControl
                  name='telegramUsername'
                  label='Профиль Telegram'
                  placeholder='Профиль Telegram'
                  disabled={!isEditing}
                  rules={{ required: true, ...VALIDATION.telegramUsername }}
                />
              ) : (
                <>
                  <TextFieldControl
                    name='login'
                    label='Логин пользователя'
                    placeholder='Логин пользователя'
                    disabled={!isEditing}
                    rules={{ required: true, ...VALIDATION.telegramUsername }}
                  />
                  <TextFieldControl
                    name='password'
                    label='Пароль'
                    type='password'
                    disabled={!isEditing}
                  />
                </>
              )}
              <TextFieldControl
                name='fullName'
                label='ФИО'
                placeholder='ФИО'
                disabled={!isEditing}
                rules={{ required: true, ...VALIDATION.fullUserName }}
              />
              <TextFieldControl
                name='email'
                label='Почта'
                placeholder='Почта'
                disabled={!isEditing}
                rules={{ ...VALIDATION.emailFull }}
              />
              <TextFieldControl
                name='phoneNumber'
                label='Номер телефона'
                placeholder='Номер телефона'
                disabled
                maskProps={{ mask: "+{7} (000) 000-00-00" }}
              />
            </Stack>
          </Grid>
          <Grid
            item
            xs={12}
            md={6}>
            <Stack spacing={2}>
              <AutocompleteControl
                options={GRADE_OPTIONS}
                name='grade'
                label='Грейд'
                placeholder='Грейд'
                disabled={!isEditing}
              />
              <TextFieldControl
                name='organization'
                label='Организация'
                placeholder='Организация'
                disabled={!isEditing}
                rules={{ ...VALIDATION.companyName }}
              />
              <TextFieldControl
                name='position'
                label='Должность'
                placeholder='Должность'
                disabled={!isEditing}
                rules={{ ...VALIDATION.position }}
              />
              <AutocompleteControl
                options={CANDIDATE_TYPE_OPTIONS}
                name='type'
                label='Тип'
                placeholder='Тип'
                disabled={!isEditing}
                rules={{ required: true }}
              />
              <TextFieldControl
                name='link'
                label='Ссылка на HH'
                placeholder='Ссылка на HH'
                disabled={!isEditing}
                rules={{ ...VALIDATION.position }}
              />
              <Stack spacing={0.5}>
                <Typography
                  variant='body14rg'
                  sx={{ color: "base.500" }}>
                  Резюме
                </Typography>
                <FileUploadButton
                  name='cvFile'
                  label='Загрузить резюме'
                  fileUrl={companyCandidate?.cvFile?.url}
                  disabled={!isEditing}
                  accept='application/pdf'
                  tooltipText='Отчет по CV'
                />
              </Stack>
            </Stack>
          </Grid>
          <VacanciesList isEditing={isEditing} />
          <Grid
            item
            xs={12}
            md={3}>
            <SaveEditButtons
              isEdited={isEditing}
              onSubmit={handleSubmit(onUpdateCompanyCandidate)}
              setIsEdited={setIsEditing}
              loading={updateCompanyCandidateLoading}
            />
          </Grid>
        </Grid>
      </Form>
    </Box>
  );
};

export default Content;
